/**
 * Making third-party text safe to interpolate into a prompt.
 *
 * Two places in this system hand untrusted text to a model. Enrichment feeds it 97 MB of machine
 * transcript written by people we have never met; the agent feeds it course descriptions and
 * retrieved transcript snippets, at the request of whoever typed the question. Both defences are
 * structural rather than hopeful — instructions live in the system message, content lives inside
 * a delimiter the system message has declared inert — and a delimiter is only a boundary for as
 * long as the content cannot close it.
 *
 * That last sentence is why this module exists once rather than twice. A security control with
 * two implementations has one that is out of date, and the out-of-date one is never the one
 * anybody reads.
 */
/**
 * Characters that can carry an instruction past a human reviewer.
 *
 * - C0/C1 control codes, minus tab and newline, which are ordinary in transcript text.
 * - Bidirectional overrides (U+202A–U+202E, U+2066–U+2069), which reorder how text renders
 *   without changing what the model reads — the "Trojan Source" trick.
 * - Unicode tag characters (U+E0000–U+E007F), invisible in every renderer, able to encode an
 *   entire ASCII payload inside what looks like plain text.
 * - Zero-width characters, which break up a string like `</transcript>` so a naive filter misses
 *   it.
 */
const DANGEROUS_CHARACTERS = 
// eslint-disable-next-line no-control-regex -- stripping control characters is the point
/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]|[\u{E0000}-\u{E007F}]/gu;
/**
 * Every delimiter either caller uses, plus every role marker a chat transcript is spelled with.
 *
 * Deliberately one list shared by both callers rather than a per-caller list. Over-removal costs
 * a `[tag removed]` in text that was never going to be quoted back; under-removal costs the
 * boundary. And a tag that only the *other* caller uses is exactly the one nobody would think to
 * add here after it starts mattering.
 */
const DELIMITER_TAGS = [
    'transcript',
    'metadata',
    'outline',
    'skills',
    'course',
    'courses',
    'lesson',
    'lessons',
    'pathway',
    'pathways',
    'catalog',
    'results',
    'tool_result',
    'instructions',
    'system',
    'developer',
    'assistant',
    'user',
];
const DELIMITER_LOOKALIKE = new RegExp(String.raw `<\s*/?\s*(?:${DELIMITER_TAGS.join('|')})\b[^>]*>`, 'giu');
/**
 * Strips what cannot be shown and neutralises what could be mistaken for our own structure.
 *
 * Note what it does *not* do: it does not remove the injection attempt itself. "Ignore all
 * previous instructions" is left in place, because in a transcript it is material to be described
 * and in a course description it is evidence worth surfacing. Deleting it would change the
 * content we were asked to reason about, and would replace a defence that works with one that
 * only works against payloads somebody already thought of. What must not survive is the payload's
 * ability to escape the block it was placed in.
 *
 * The zero-width strip runs first, so `</trans\u200Bcript>` is caught by the same pass that
 * catches the plain spelling.
 */
export function sanitizeForPrompt(text) {
    return text
        .replace(DANGEROUS_CHARACTERS, '')
        .replace(DELIMITER_LOOKALIKE, '[tag removed]')
        .replace(/\r\n?/gu, '\n')
        .replace(/\n{3,}/gu, '\n\n')
        .trim();
}
/**
 * An attribute value that cannot terminate its own quoting.
 *
 * Applied to ids and file names, which are the one part of a delimiter that comes from data. A
 * file name of `"><transcript id="evil` would otherwise open a second block from inside the
 * opening tag of the first.
 */
export function safeAttribute(value) {
    return value.replace(/[^A-Za-z0-9._-]/gu, '');
}
/** Wraps sanitised content in a delimiter the system message has already declared inert. */
export function wrapForPrompt(tag, id, body) {
    return `<${tag} id="${safeAttribute(id)}">\n${body}\n</${tag}>`;
}
//# sourceMappingURL=prompt-safety.js.map